import {
  ACHIEVEMENTS,
  achievementProgress,
  emptyStats,
  FAST_CLEAR_MS,
  levelStats,
  wardrobeStats,
  type AchievementDef,
  type AchievementStats,
} from "@nano/content";
import type { HouseId } from "@nano/game-config";
import { ownedItems } from "./save/progress.ts";
import type { SaveData } from "./save/types.ts";

export type AchievementStatus = "locked" | "claimable" | "claimed" | "earned";

export interface AchievementView {
  readonly def: AchievementDef;
  readonly status: AchievementStatus;
  readonly current: number;
}

/** What this device knows (local progress may run ahead of the server). */
export function localStats(save: SaveData): AchievementStats {
  const levels: { houseId: HouseId; levelId: number; bestStars: number }[] = [];
  let fastClears = 0;
  for (const [houseId, houseLevels] of Object.entries(save.progress) as [HouseId, SaveData["progress"][HouseId]][]) {
    for (const [levelId, level] of Object.entries(houseLevels ?? {})) {
      levels.push({ houseId, levelId: Number(levelId), bestStars: level.bestStars });
      if (level.bestTimeMs != null && level.bestTimeMs < FAST_CLEAR_MS) fastClears += 1;
    }
  }
  return {
    ...emptyStats(),
    ...levelStats(levels),
    ...wardrobeStats(ownedItems(save), save.equipped),
    recoverySaved: save.server?.hasRecoveryCode ? 1 : 0,
    fastClears,
    playDays: save.stats.playDayCount,
  };
}

export function achievementViews(save: SaveData): AchievementView[] {
  const stats = localStats(save);
  const claimed = new Set(save.server?.claimedAchievements ?? []);
  return ACHIEVEMENTS.map((def) => {
    const { current, reached } = achievementProgress(def, stats);
    let status: AchievementStatus = "locked";
    if (def.verified && claimed.has(def.id)) status = "claimed";
    else if (reached) status = def.verified ? "claimable" : "earned";
    return { def, status, current };
  });
}

export function claimableCount(save: SaveData): number {
  return achievementViews(save).filter((view) => view.status === "claimable").length;
}

/** Reached (or already paid) achievements whose moment has not been shown yet. */
export function unannounced(save: SaveData): AchievementDef[] {
  const announced = new Set(save.announcedAchievements);
  return achievementViews(save)
    .filter((view) => view.status !== "locked" && !announced.has(view.def.id))
    .map((view) => view.def);
}
